/**
 * Email Popup Component
 * Handles display timing, form submission, and localStorage persistence
 */

class EmailPopup extends HTMLElement {
  constructor() {
    super();
    this.overlay = this.querySelector('.email-popup-overlay');
    this.popup = this.querySelector('.email-popup');
    this.closeBtn = this.querySelector('.email-popup__close');
    this.form = this.querySelector('.email-popup__form');
    this.successMessage = this.querySelector('.email-popup__success');
    this.errorMessage = this.querySelector('.email-popup__error');
    
    // Get settings from data attributes
    this.delay = parseInt(this.dataset.delay) || 3000;
    this.storageKey = 'emailPopupDismissed';
    this.subscribedKey = 'emailPopupSubscribed';
    this.testMode = this.dataset.testMode === 'true';
    this.expiryDays = parseInt(this.dataset.expiryDays) || 30;
  }

  connectedCallback() {
    this.init();
  }

  init() {
    // Check if we should show the popup
    if (!this.shouldShowPopup()) {
      return;
    }

    // Set up event listeners
    this.setupEventListeners();

    // Show popup after delay
    setTimeout(() => this.showPopup(), this.delay);
  }

  shouldShowPopup() {
    // In test mode (Shopify editor), always show
    if (this.testMode || Shopify.designMode) {
      return true;
    }

    // Check if already subscribed
    if (localStorage.getItem(this.subscribedKey)) {
      return false;
    }

    // Check if dismissed and not expired
    const dismissedData = localStorage.getItem(this.storageKey);
    if (dismissedData) {
      try {
        const { timestamp } = JSON.parse(dismissedData);
        const expiryTime = this.expiryDays * 24 * 60 * 60 * 1000;
        if (Date.now() - timestamp < expiryTime) {
          return false;
        }
      } catch (e) {
        // Invalid data, clear it
        localStorage.removeItem(this.storageKey);
      }
    }

    return true;
  }

  setupEventListeners() {
    // Close button
    if (this.closeBtn) {
      this.closeBtn.addEventListener('click', () => this.hidePopup(true));
    }

    // Click outside to close
    if (this.overlay) {
      this.overlay.addEventListener('click', (e) => {
        if (e.target === this.overlay) {
          this.hidePopup(true);
        }
      });
    }

    // Escape key to close
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.overlay?.classList.contains('active')) {
        this.hidePopup(true);
      }
    });

    // Form submission
    if (this.form) {
      this.form.addEventListener('submit', (e) => this.handleSubmit(e));
    }
  }

  showPopup() {
    if (this.overlay) {
      this.overlay.classList.add('active');
      document.body.style.overflow = 'hidden';
      
      // Focus the email input for accessibility
      const emailInput = this.querySelector('input[type="email"]');
      if (emailInput) {
        setTimeout(() => emailInput.focus(), 400);
      }
    }
  }

  hidePopup(saveDismissal = false) {
    if (this.overlay) {
      this.overlay.classList.remove('active');
      document.body.style.overflow = '';
      
      if (saveDismissal && !Shopify.designMode) {
        localStorage.setItem(this.storageKey, JSON.stringify({
          timestamp: Date.now()
        }));
      }
    }
  }

  async handleSubmit(e) {
    e.preventDefault();
    
    const emailInput = this.form.querySelector('input[type="email"]');
    const submitBtn = this.form.querySelector('button[type="submit"]');
    const email = emailInput?.value;

    if (!email) return;

    // Disable form during submission
    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.textContent = '...';
    }

    // Hide any existing errors
    if (this.errorMessage) {
      this.errorMessage.classList.remove('active');
    }

    try {
      // Use Shopify's customer API for newsletter signup
      const formData = new FormData();
      formData.append('contact[email]', email);
      formData.append('contact[tags]', 'newsletter,popup');
      formData.append('form_type', 'customer');
      formData.append('utf8', '✓');

      const response = await fetch('/contact', {
        method: 'POST',
        body: formData,
        headers: {
          'Accept': 'application/json'
        }
      });

      // Show success regardless (Shopify may redirect or return various responses)
      this.showSuccess();
      
      // Mark as subscribed
      if (!Shopify.designMode) {
        localStorage.setItem(this.subscribedKey, 'true');
      }

      // Auto close after showing success
      setTimeout(() => this.hidePopup(false), 3000);

    } catch (error) {
      console.error('Email popup submission error:', error);
      
      // Show error message
      if (this.errorMessage) {
        this.errorMessage.textContent = 'Something went wrong. Please try again.';
        this.errorMessage.classList.add('active');
      }
      
      // Re-enable form
      if (submitBtn) {
        submitBtn.disabled = false;
        submitBtn.textContent = this.dataset.buttonLabel || 'Subscribe';
      }
    }
  }

  showSuccess() {
    if (this.form) {
      this.form.classList.add('hidden');
    }
    if (this.successMessage) {
      this.successMessage.classList.add('active');
    }
  }
}

// Register custom element
if (!customElements.get('email-popup')) {
  customElements.define('email-popup', EmailPopup);
}

